import { CartAddEvent } from '@theme/events';
import { getCartFromSections, getCartSectionIds } from '@theme/caliart-cart-state';

class CaliartCartAdd extends HTMLElement {
  connectedCallback() {
    this.onclick = async (event) => {
      const button = event.target.closest('button[data-variant]');
      if (!button || button.disabled) return;
      button.disabled = true;
      button.textContent = 'Adding…';
      const error = this.querySelector('[role="alert"]');
      if (error) error.textContent = '';
      let added = false;
      try {
        const sections = getCartSectionIds();
        const response = await fetch(Theme.routes.cart_add_url, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({items: [{id: button.dataset.variant, quantity: 1}], sections, sections_url: location.pathname})
        });
        const result = await response.json().catch(() => null);
        if (!response.ok || result?.status) throw new Error(result?.description || 'Unable to add this item. Please try again.');
        // A successful POST must never be retried just because its JSON or cart refresh failed.
        added = true;
        let cart = getCartFromSections(result?.sections);
        if (!cart) try {
          const cartResponse = await fetch(Theme.routes.cart_url + '.js', { cache: 'no-store' });
          if (!cartResponse.ok) throw new Error('Cart refresh failed.');
          cart = await cartResponse.json();
          if (!Number.isInteger(cart?.item_count) || cart.item_count < 0 || !Array.isArray(cart.items)) {
            throw new Error('Invalid cart response.');
          }
        } catch {
          // Bundled cart section HTML also carries Shopify's full, uncapped item count.
          const itemCount = this.#getSectionItemCount(result?.sections);
          if (itemCount === undefined) throw new Error('Item added. Refresh to see your updated cart.');
          cart = { item_count: itemCount };
        }
        const variantId = button.dataset.variant;
        const addedItem = (cart.items ?? result?.items ?? []).find(
          (item) => String(item.variant_id ?? item.id) === variantId
        );
        button.textContent = 'Added ✓';
        document.dispatchEvent(new CartAddEvent(cart, 'caliart-pairing', {
          source: 'caliart-pairing',
          sections: result?.sections,
          // Non-product-form updates replace the cart bubble and cached count, rather than adding a delta.
          itemCount: cart.item_count,
          variantId,
          productId: addedItem?.product_id?.toString(),
        }));
      } catch (e) {
        if (error) error.textContent = added ? 'Item added. Refresh to see your updated cart.' : e.message;
        button.textContent = added ? 'Added ✓' : '+ Add';
        button.disabled = added;
      }
    };
  }

  #getSectionItemCount(sections) {
    for (const html of Object.values(sections ?? {})) {
      if (typeof html !== 'string') continue;
      const text = new DOMParser().parseFromString(html, 'text/html')
        .querySelector('[ref="cartItemCount"]')?.textContent?.trim();
      if (text && /^\d+$/.test(text) && Number.isSafeInteger(Number(text))) return Number(text);
    }
  }
}
if (!customElements.get('caliart-cart-add')) customElements.define('caliart-cart-add', CaliartCartAdd);
